import { useMemo, useCallback } from 'react';
import type { ComplianceIssue } from '@/services/compliance.service';

/**
 * Valid compliance issue types for counting and filtering
 * Note: H, I, K are placeholders for future types
 */
export const VALID_ISSUE_TYPES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'] as const;
export type ValidIssueType = typeof VALID_ISSUE_TYPES[number];

/**
 * Issue type groups for menu navigation
 */
export const ISSUE_TYPE_GROUPS = {
  vat: ['A'] as ValidIssueType[],
  duplicates: ['C', 'D'] as ValidIssueType[],
  gl: ['E', 'J'] as ValidIssueType[]  // Mixed GL and Same GL
} as const;

/**
 * All individual and grouped issue types for menu display
 */
export const ALL_MENU_ISSUE_TYPES = [
  'all',
  'vat',
  ...VALID_ISSUE_TYPES,
  'duplicates', 
  'gl'
] as const;

export type MenuIssueType = typeof ALL_MENU_ISSUE_TYPES[number];

/**
 * Utility function to validate if a string is a valid issue type
 */
export function isValidIssueType(type: string): type is ValidIssueType {
  return VALID_ISSUE_TYPES.includes(type as ValidIssueType);
}

/**
 * Calculate issue counts by type from filtered compliance issues
 * Extracted from ComplianceFiltersPanel for reuse in navigation menu
 * Excludes issues with status 'ok' since they are resolved/acceptable
 */
export function calculateIssueTypeCounts(issues: ComplianceIssue[]): Record<string, number> {
  const counts: Record<string, number> = {};
  
  issues.forEach(issue => {
    // Exclude issues with status 'ok' as they are resolved/acceptable
    if (issue.status === 'ok') {
      return;
    }
    
    const type = issue.type || issue.issue_type;
    if (type) {
      counts[type] = (counts[type] || 0) + 1;
    }
  });
  
  return counts;
}

/**
 * Calculate grouped issue type counts (VAT, Duplicates, Dates)
 */
export function calculateGroupedCounts(typeCounts: Record<string, number>): Record<string, number> {
  const groupedCounts: Record<string, number> = {};
  
  // Calculate group totals
  Object.entries(ISSUE_TYPE_GROUPS).forEach(([groupName, types]) => {
    groupedCounts[groupName] = types.reduce((sum, type) => sum + (typeCounts[type] || 0), 0);
  });
  
  // Add total count
  groupedCounts.all = Object.values(typeCounts).reduce((sum, count) => sum + count, 0);
  
  return groupedCounts;
}

/**
 * React hook for calculating compliance issue counts with memoization
 * Replicates the optimized logic from ComplianceFiltersPanel
 */
export function useComplianceCounts(filteredIssues: ComplianceIssue[]) {
  // Calculate individual type counts
  const countsByType = useMemo(() => {
    return calculateIssueTypeCounts(filteredIssues);
  }, [filteredIssues]);
  
  // Calculate grouped counts (VAT, Duplicates, Dates, All)
  const groupedCounts = useMemo(() => {
    return calculateGroupedCounts(countsByType);
  }, [countsByType]);
  
  // Combined counts for easy lookup
  const allCounts = useMemo(() => {
    return { ...countsByType, ...groupedCounts };
  }, [countsByType, groupedCounts]);
  
  // Optimized count getter function
  const getCount = useCallback((issueType: string): number => {
    return allCounts[issueType] || 0;
  }, [allCounts]);
  
  // Check if an issue type has any issues
  const hasIssues = useCallback((issueType: string): boolean => {
    return getCount(issueType) > 0;
  }, [getCount]);
  
  return {
    countsByType,
    groupedCounts,
    allCounts,
    getCount,
    hasIssues,
    totalIssues: groupedCounts.all || 0
  };
}

/**
 * Get display label for issue types in navigation menu
 */
export function getIssueTypeLabel(issueType: string): string {
  const labels: Record<string, string> = {
    all: 'Issues Summary',
    vat: 'VAT Issues',
    A: 'VAT Issues',
    B: 'Mixed VAT',
    C: 'Duplicate Bills',
    D: 'Spend vs Bill',
    E: 'Mixed GL',
    F: 'Aged Payables',
    G: 'Aged Receivables',
    H: 'Unreconciled Bank Transactions',
    I: 'Serial Checker',
    J: 'Same GL',
    K: 'Clearing / Control',
    duplicates: 'Duplicate Issues',
    gl: 'GL Issues'
  };

  return labels[issueType] || issueType;
}